'use client';

import { useCallback, useContext, useRef } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';
import { useLatest, useUpdateEffect } from 'react-use';

import { useDebouncedNetworkState } from '@/components/providers/debounced-network-state-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import {
  buildInterruptedEventPayload,
  cleanupPendingResponse,
  getHadStreamingStarted,
  getIsRetry,
} from '@/hooks/analytics-utils';
import { useAnalytics } from '@/hooks/use-analytics';
import { ResponseTrackingContext } from '@/hooks/use-response-tracking';
import { ABORT_REASONS, CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { queueDeletion } from '@/lib/utils/deletion-queue';
import { getThreadUuid } from '@/lib/utils/thread';

export function useAutoResumeOnReconnect() {
  const isReadOnly = useReadOnly();
  const api = useAui();
  const { online, previous } = useDebouncedNetworkState();
  const hasResumedRef = useRef(false);
  const threadListItem = useAuiState(state => state.threadListItem);
  const threadListItemRef = useLatest(threadListItem);
  const { trackChatEvent } = useAnalytics();
  const responseTrackingContext = useContext(ResponseTrackingContext);
  const responseTrackingContextRef = useLatest(responseTrackingContext);

  const fireInterruptedForPendingResponses = useCallback(() => {
    const context = responseTrackingContextRef.current;
    const pendingResponses = context?.pendingResponsesRef.current;
    if (!pendingResponses?.size) return;

    pendingResponses.forEach((startTime, threadId) => {
      const payload = buildInterruptedEventPayload({
        threadId,
        startTime,
        hadStreamingStarted: getHadStreamingStarted(context, threadId),
        abortReason: ABORT_REASONS.CONNECTION_ERROR,
        isRetry: getIsRetry(context, threadId),
      });

      trackChatEvent(CHAT_ANALYTICS_EVENTS.RESPONSE_INTERRUPTED, payload);
      cleanupPendingResponse(context, threadId);
    });
  }, [responseTrackingContextRef, trackChatEvent]);

  const handleReconnect = useCallback(async () => {
    if (hasResumedRef.current) return;
    hasResumedRef.current = true;

    try {
      const thread = api.thread;
      const { messages } = thread.getState();
      const lastMessage = messages.at(-1);
      if (!lastMessage) return;

      const rawThreadId = threadListItemRef.current?.id;
      const threadId = rawThreadId ? getThreadUuid(rawThreadId) : undefined;

      if (lastMessage.role === 'user') {
        trackChatEvent(CHAT_ANALYTICS_EVENTS.RESPONSE_AUTO_RETRIED, { threadId });
        thread.startRun({ parentId: lastMessage.id });
      } else if (
        lastMessage.role === 'assistant' &&
        (lastMessage.status?.type === 'incomplete' || lastMessage.status?.type === 'running')
      ) {
        if (rawThreadId) {
          await queueDeletion(rawThreadId, lastMessage.id);
        }
        const parentMessage = messages.at(-2);
        if (parentMessage?.role === 'user') {
          trackChatEvent(CHAT_ANALYTICS_EVENTS.RESPONSE_AUTO_RETRIED, { threadId });

          /**
           * Use message.reload() instead of thread.startRun() to properly handle the retry.
           *
           * Why this is needed:
           * - `queueDeletion()` only queues the message for backend deletion sync
           * - It does NOT remove the message from the runtime's message state
           * - When using `thread.startRun({ parentId })`, the AI SDK's `createStreamingUIMessageState`
           *   may reuse the old assistant message (if it's still in state) along with its tool call parts
           * - This causes "Duplicate key toolCallId-X in tapResources" errors when new tool calls
           *   arrive with the same or different IDs
           *
           * Using `message.reload()` ensures the runtime properly handles replacing the
           * old assistant message with a new response by passing both parentId and sourceId,
           * which tells the runtime which message is being replaced.
           */
          const messageRuntime = thread.message({ id: lastMessage.id });
          messageRuntime.reload();
        }
      }
    } catch (error) {
      console.error('Failed to auto-resume on reconnect:', error);
    }
  }, [api, threadListItemRef, trackChatEvent]);

  useUpdateEffect(() => {
    if (isReadOnly) return;
    if (online && previous === false) {
      handleReconnect();
    } else if (!online && previous === true) {
      hasResumedRef.current = false;
      try {
        fireInterruptedForPendingResponses();
      } catch (error) {
        console.error('Failed to track interrupted responses:', error);
      }
    }
  }, [isReadOnly, online, previous, handleReconnect, fireInterruptedForPendingResponses]);
}
